import { useCallback, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import type { LpState } from '../../types/lp'
import type { StageAllocation } from '../../types/strategy'
import { buildLpTx } from '../../api/lp'
import { deserializeV0 } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { useSplBalance } from '../../hooks/useSplBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'
import { MINTS } from '../../api/trade'

const RAYDIUM_URL = 'https://raydium.io/portfolio/'
const ORCA_URL    = 'https://www.orca.so/portfolio'

const REMOVE_PRESETS = [25, 50, 75, 100] as const

interface Props {
  balances: WalletBalance[] | null
  positions: LpState[]
  lpStage?: StageAllocation | null
}

function usd(v: string | number | null | undefined) {
  if (v == null) return '—'
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (!Number.isFinite(n)) return '—'
  return '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
}

export default function LpForm({ balances: _balances, positions, lpStage }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()

  const pos = positions.length > 0
    ? positions.reduce((best, p) =>
        parseFloat(p.lpValueUsd) > parseFloat(best.lpValueUsd) ? p : best
      )
    : null

  const protocol = (pos?.provider ?? 'raydium') as 'raydium' | 'orca'

  const [mode, setMode]         = useState<'add' | 'remove'>('add')
  const [solAmount, setSolAmount] = useState('')
  const [removePct, setRemovePct] = useState<number>(50)
  const [result, setResult]     = useState<TradeResult>({ kind: 'idle' })

  const splSol = useSplBalance(publicKey, MINTS.SOL)
  const solNum = Number(splSol.decimal) || 0

  const maxSol = () => {
    const raw = solNum - 0.01
    if (raw > 0) setSolAmount(raw.toFixed(6))
  }

  const currentPrice = pos ? parseFloat(pos.currentPrice) : 0
  const estimatedUsdc = (() => {
    const sol = parseFloat(solAmount)
    if (!sol || !currentPrice) return null
    return (sol * currentPrice).toFixed(2)
  })()

  const handleSubmit = useCallback(async () => {
    if (!portfolioId || !publicKey) return
    if (portfolioId !== publicKey.toBase58()) {
      setResult({ kind: 'error', message: 'Connected wallet does not match your session' })
      return
    }
    if (!pos) {
      setResult({ kind: 'error', message: 'No LP position found — create one first via the Strategy Allocation panel.' })
      return
    }

    if (mode === 'add') {
      const sol = parseFloat(solAmount)
      if (!sol || sol <= 0) {
        setResult({ kind: 'error', message: 'Enter a SOL amount to add' })
        return
      }
      const baseAmountLamports = Math.floor(sol * 1e9).toString()
      setResult({ kind: 'pending', label: 'Building transaction…' })
      try {
        const { txBase64 } = await buildLpTx(portfolioId, {
          protocol,
          operation: 'add',
          poolId: pos.poolId,
          walletPubkey: publicKey.toBase58(),
          positionMint: pos.positionMint,
          baseAmountLamports,
          currentPrice: pos.currentPrice,
        })
        const tx = deserializeV0(txBase64)
        setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
        const submitted = await signAndSubmit(tx)
        setResult({
          kind: 'success',
          signature: submitted.signature,
          summary: `Added ${sol} SOL to ${protocol === 'raydium' ? 'Raydium' : 'Orca'} LP — position updates in ~30s`,
        })
        setSolAmount('')
      } catch (e) {
        setResult({ kind: 'error', message: friendlyTxError(e) })
      }
    } else {
      setResult({ kind: 'pending', label: 'Building transaction…' })
      try {
        const { txBase64 } = await buildLpTx(portfolioId, {
          protocol,
          operation: 'remove',
          poolId: pos.poolId,
          walletPubkey: publicKey.toBase58(),
          positionMint: pos.positionMint,
          removePct,
          currentPrice: pos.currentPrice,
        })
        const tx = deserializeV0(txBase64)
        setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
        const submitted = await signAndSubmit(tx)
        setResult({
          kind: 'success',
          signature: submitted.signature,
          summary: `Removed ${removePct}% from ${protocol === 'raydium' ? 'Raydium' : 'Orca'} LP — position updates in ~30s`,
        })
      } catch (e) {
        setResult({ kind: 'error', message: friendlyTxError(e) })
      }
    }
  }, [portfolioId, publicKey, pos, mode, solAmount, removePct, protocol, signAndSubmit, friendlyTxError])

  const canSubmit = result.kind !== 'pending' && !!pos && connected && (
    mode === 'add' ? parseFloat(solAmount) > 0 : true
  )

  const totalValue = positions.reduce((s, p) => s + parseFloat(p.lpValueUsd || '0'), 0)

  return (
    <div className="space-y-3">
      {/* Position cards */}
      <div className="grid grid-cols-2 gap-2">
        <PosCard
          title="SOL leg"
          mint={MINTS.SOL}
          symbol="SOL"
          amount={pos ? parseFloat(pos.token0Amount).toFixed(3) : '—'}
          usd={pos ? usd(parseFloat(pos.lpValueUsd) / 2) : '—'}
          rate={pos?.netLpApr ? `+${(parseFloat(pos.netLpApr) * 100).toFixed(1)}% APR` : ''}
          rateClass="text-accent-green"
        />
        <PosCard
          title="USDC leg"
          mint={MINTS.USDC}
          symbol="USDC"
          amount={pos ? parseFloat(pos.token1Amount).toFixed(2) : '—'}
          usd={pos ? usd(parseFloat(pos.lpValueUsd) / 2) : '—'}
          rate={pos && !pos.inRange ? 'Out of range' : ''}
          rateClass="text-amber-400"
        />
      </div>

      {/* Total + pending fees */}
      <div className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-[11px]">
        <span className="text-gray-500">Total LP value</span>
        <span className="text-ink font-semibold">{totalValue > 0 ? usd(totalValue) : '—'}</span>
      </div>

      {!pos && (
        <div className="rounded-lg border border-accent-yellow/30 bg-accent-yellow/10 px-3 py-2 text-[11px] text-accent-yellow leading-relaxed">
          No LP position found. Create one first via{' '}
          <a href={RAYDIUM_URL} target="_blank" rel="noopener noreferrer" className="underline">Raydium</a>
          {' '}or{' '}
          <a href={ORCA_URL} target="_blank" rel="noopener noreferrer" className="underline">Orca</a>
          {lpStage && ` (target ${usd(lpStage.amountUsd)})`}.
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex gap-1 rounded-lg bg-surface-hover p-1">
        <button
          onClick={() => { setMode('add'); setSolAmount(''); setResult({ kind: 'idle' }) }}
          className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${mode === 'add' ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted'}`}
        >
          <TokenIcon symbol="SOL" mint={MINTS.SOL} size={14} />
          Add Liquidity
        </button>
        <button
          onClick={() => { setMode('remove'); setResult({ kind: 'idle' }) }}
          disabled={!pos}
          className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-40
            ${mode === 'remove' ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted'}`}
        >
          <TokenIcon symbol="SOL" mint={MINTS.SOL} size={14} />
          Remove Liquidity
        </button>
      </div>

      {/* Add mode */}
      {mode === 'add' && (
        <div className="rounded-lg border border-fringe bg-surface-hover p-3">
          <div className="mb-1 flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-wider text-gray-500">SOL to add</label>
            <button type="button" onClick={maxSol} className="text-[10px] text-accent-purple hover:underline">
              MAX {solNum > 0.01 ? `${(solNum - 0.01).toFixed(3)} SOL` : ''}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <TokenIcon symbol="SOL" mint={MINTS.SOL} size={18} />
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={solAmount}
              onChange={e => setSolAmount(e.target.value)}
              className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
            />
          </div>
          {estimatedUsdc && (
            <div className="mt-1 text-[10px] text-gray-500">
              ≈ {estimatedUsdc} USDC paired at current price
            </div>
          )}
        </div>
      )}

      {/* Remove mode */}
      {mode === 'remove' && (
        <div className="rounded-lg border border-fringe bg-surface-hover p-3">
          <div className="mb-2 text-[10px] uppercase tracking-wider text-gray-500">Remove %</div>
          <div className="flex gap-1.5">
            {REMOVE_PRESETS.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setRemovePct(p)}
                className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${
                  removePct === p ? 'bg-accent-purple/25 text-ink' : 'bg-surface-card text-ink-muted hover:bg-surface-hover'
                }`}
              >
                {p}%
              </button>
            ))}
          </div>
          {pos && (
            <div className="mt-2 text-[11px] text-gray-500">
              Withdrawing {usd(parseFloat(pos.lpValueUsd) * removePct / 100)} of {usd(pos.lpValueUsd)}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
      >
        {result.kind === 'pending'
          ? 'Working…'
          : !connected
          ? `Connect & ${mode === 'add' ? 'Add' : 'Remove'}`
          : !pos
          ? 'No position found'
          : mode === 'add'
          ? `Sign & Add on ${protocol === 'raydium' ? 'Raydium' : 'Orca'}`
          : `Sign & Remove ${removePct}% on ${protocol === 'raydium' ? 'Raydium' : 'Orca'}`}
      </button>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />
    </div>
  )
}

function PosCard({ title, mint, symbol, amount, usd: usdVal, rate, rateClass }: {
  title: string; mint: string; symbol: string; amount: string; usd: string; rate: string; rateClass: string;
}) {
  return (
    <div className="rounded-lg bg-surface-hover p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{title}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <TokenIcon symbol={symbol} mint={mint} size={16} />
        <span className="text-sm font-semibold text-ink">{amount} {symbol}</span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>{usdVal}</span>
        {rate && <span className={rateClass}>{rate}</span>}
      </div>
    </div>
  )
}
